// Feature Detail Page - View and vote on a specific feature request
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Button,
  Tooltip,
  Breadcrumbs,
  Link,
  Divider,
  Avatar,
  TextField,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  ThumbUp,
  ThumbUpOutlined,
  Lightbulb,
  Schedule,
  CheckCircle,
  TrendingUp,
  NewReleases,
  Person,
  CalendarToday,
  Send,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePortal } from './PortalLayout';
import { featuresService } from '@/services/collections/features';
import type { FeatureRequest, FeatureStatus } from '@/types';

const STATUS_CONFIG: Record<FeatureStatus, { label: string; color: string; icon: React.ReactNode; description: string }> = {
  under_review: { label: 'Under Review', color: '#94a3b8', icon: <Lightbulb />, description: 'We are reviewing this idea' },
  planned: { label: 'Planned', color: '#3b82f6', icon: <Schedule />, description: 'This feature is on our roadmap' },
  in_progress: { label: 'In Progress', color: '#f59e0b', icon: <TrendingUp />, description: 'We are actively working on this' },
  released: { label: 'Released', color: '#10b981', icon: <CheckCircle />, description: 'This feature has been released' },
  declined: { label: 'Declined', color: '#ef4444', icon: <NewReleases />, description: 'We will not be implementing this' },
};

const commentSchema = z.object({
  content: z.string().min(5, 'Comment must be at least 5 characters'),
  email: z.string().email('Valid email required'),
  name: z.string().optional(),
});

type CommentFormData = z.infer<typeof commentSchema>;

interface Comment {
  id: string;
  content: string;
  authorName: string;
  authorEmail: string;
  createdAt: Date;
}

export const FeatureDetailPage = () => {
  const { featureId } = useParams<{ featureId: string }>();
  const navigate = useNavigate();
  const { organization, project, branding } = usePortal();

  const [feature, setFeature] = useState<FeatureRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasVoted, setHasVoted] = useState(false);
  const [voting, setVoting] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [submittingComment, setSubmittingComment] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
  });

  // Load voted features from localStorage
  useEffect(() => {
    if (featureId) {
      const stored = localStorage.getItem(`voted_${organization?.id}`);
      if (stored) {
        const votedIds = JSON.parse(stored);
        setHasVoted(votedIds.includes(featureId));
      }
    }
  }, [organization?.id, featureId]);

  // Fetch feature
  useEffect(() => {
    const fetchFeature = async () => {
      if (!organization || !project || !featureId) return;

      setLoading(true);
      try {
        const data = await featuresService.getById(featureId);
        setFeature(data);

        // Mock comments for now (would be a subcollection in production)
        setComments([]);
      } catch (error) {
        console.error('Error fetching feature:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeature();
  }, [organization, project, featureId]);

  // Get or create visitor ID
  const getVisitorId = (): string => {
    let visitorId = localStorage.getItem('visitor_id');
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('visitor_id', visitorId);
    }
    return visitorId;
  };

  // Handle vote
  const handleVote = async () => {
    if (!feature || voting) return;

    const visitorId = getVisitorId();
    setVoting(true);

    try {
      if (hasVoted) {
        await featuresService.unvote(feature.id, visitorId);
        setFeature((prev) => prev ? { ...prev, voteCount: prev.voteCount - 1 } : null);
        setHasVoted(false);

        // Update localStorage
        const stored = localStorage.getItem(`voted_${organization?.id}`);
        const votedIds = stored ? JSON.parse(stored) : [];
        localStorage.setItem(
          `voted_${organization?.id}`,
          JSON.stringify(votedIds.filter((id: string) => id !== feature.id))
        );
      } else {
        await featuresService.vote(feature.id, visitorId);
        setFeature((prev) => prev ? { ...prev, voteCount: prev.voteCount + 1 } : null);
        setHasVoted(true);

        // Update localStorage
        const stored = localStorage.getItem(`voted_${organization?.id}`);
        const votedIds = stored ? JSON.parse(stored) : [];
        localStorage.setItem(`voted_${organization?.id}`, JSON.stringify([...votedIds, feature.id]));
      }
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setVoting(false);
    }
  };

  // Handle comment submit
  const onSubmitComment = async (data: CommentFormData) => {
    if (!feature) return;

    setSubmittingComment(true);
    try {
      // In production, this would save to a subcollection
      const newComment: Comment = {
        id: `comment_${Date.now()}`,
        content: data.content,
        authorName: data.name || 'Anonymous',
        authorEmail: data.email,
        createdAt: new Date(),
      };
      setComments((prev) => [...prev, newComment]);
      reset();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (timestamp: { toDate?: () => Date } | Date | undefined) => {
    if (!timestamp) return '';
    let date: Date;
    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      return '';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: branding.primaryColor }} />
      </Box>
    );
  }

  if (!feature) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Feature Not Found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          This feature request doesn't exist or has been removed.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/p/${organization?.slug}/ideas`)}
          sx={{ bgcolor: branding.primaryColor }}
        >
          Back to Ideas
        </Button>
      </Box>
    );
  }

  const statusConfig = STATUS_CONFIG[feature.status];

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to={`/p/${organization?.slug}`} underline="hover" color="inherit">
          Home
        </Link>
        <Link component={RouterLink} to={`/p/${organization?.slug}/ideas`} underline="hover" color="inherit">
          Feature Ideas
        </Link>
        <Typography color="text.primary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {feature.title}
        </Typography>
      </Breadcrumbs>

      {/* Back button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back
      </Button>

      {/* Main Content */}
      <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, mb: 3 }}>
        {/* Status Badge */}
        <Chip
          icon={statusConfig.icon as React.ReactElement}
          label={statusConfig.label}
          sx={{
            mb: 2,
            bgcolor: alpha(statusConfig.color, 0.1),
            color: statusConfig.color,
            fontWeight: 'medium',
            '& .MuiChip-icon': { color: statusConfig.color },
          }}
        />

        {/* Title */}
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {feature.title}
        </Typography>

        {/* Meta */}
        <Stack direction="row" spacing={3} sx={{ mb: 3, color: 'text.secondary', flexWrap: 'wrap', gap: 1 }}>
          {feature.submitterName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              <Typography variant="body2">{feature.submitterName}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 18 }} />
            <Typography variant="body2">{formatDate(feature.createdAt)}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Description */}
        <Typography variant="body1" sx={{ mb: 3, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
          {feature.description}
        </Typography>

        {/* Tags */}
        {feature.tags?.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            {feature.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
          </Stack>
        )}

        {/* Planned Release */}
        {feature.plannedRelease && (
          <Box sx={{ mb: 3, p: 2, bgcolor: alpha(branding.primaryColor, 0.05), borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Planned Release:</strong> {feature.plannedRelease}
            </Typography>
          </Box>
        )}

        {/* Release Notes */}
        {feature.status === 'released' && feature.releaseNotes && (
          <Box sx={{ mb: 3, p: 2, bgcolor: alpha('#10b981', 0.05), borderRadius: 2, border: '1px solid', borderColor: alpha('#10b981', 0.2) }}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              Release Notes
            </Typography>
            <Typography variant="body2">{feature.releaseNotes}</Typography>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Vote Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Tooltip title={hasVoted ? 'Remove your vote' : 'Vote for this feature'}>
            <Button
              variant={hasVoted ? 'contained' : 'outlined'}
              startIcon={hasVoted ? <ThumbUp /> : <ThumbUpOutlined />}
              onClick={handleVote}
              disabled={voting}
              sx={{
                minWidth: 150,
                bgcolor: hasVoted ? branding.primaryColor : 'transparent',
                borderColor: branding.primaryColor,
                color: hasVoted ? 'white' : branding.primaryColor,
                '&:hover': {
                  bgcolor: hasVoted ? branding.primaryColor : alpha(branding.primaryColor, 0.1),
                  borderColor: branding.primaryColor,
                },
              }}
            >
              {hasVoted ? 'Voted' : 'Vote'}
            </Button>
          </Tooltip>
          <Typography variant="h5" fontWeight="bold">
            {feature.voteCount} {feature.voteCount === 1 ? 'vote' : 'votes'}
          </Typography>
        </Box>

        {/* Status explanation */}
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
          {statusConfig.description}
        </Typography>
      </Paper>

      {/* Comments Section */}
      <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="medium" gutterBottom>
          Discussion ({comments.length})
        </Typography>

        {/* Comment Form */}
        <Box component="form" onSubmit={handleSubmit(onSubmitComment)} sx={{ mb: 3 }}>
          <TextField
            {...register('content')}
            fullWidth
            multiline
            rows={3}
            placeholder="Share your thoughts or add more context..."
            error={!!errors.content}
            helperText={errors.content?.message}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              {...register('email')}
              size="small"
              placeholder="Your email"
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            <TextField
              {...register('name')}
              size="small"
              placeholder="Your name (optional)"
              sx={{ flexGrow: 1, minWidth: 150 }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={submittingComment}
              startIcon={<Send />}
              sx={{ bgcolor: branding.primaryColor }}
            >
              {submittingComment ? 'Posting...' : 'Post'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Comments List */}
        {comments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No comments yet. Be the first to share your thoughts!
          </Typography>
        ) : (
          <Stack spacing={2}>
            {comments.map((comment) => (
              <Box key={comment.id} sx={{ display: 'flex', gap: 2 }}>
                <Avatar sx={{ bgcolor: branding.primaryColor }}>
                  {comment.authorName[0].toUpperCase()}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2">{comment.authorName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(comment.createdAt)}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{comment.content}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};
